import { Condition } from "./enums/condition";
import { Expression } from "./expressions/abstract.expression";
import { BooleanExpression } from "./expressions/booleanExpression";
import { ConstantExpression } from "./expressions/constantExpression";
import { TopExpression } from "./expressions/topExpression";
import { SkipExpression } from "./expressions/skipExpression";
import { CountExpression } from "./expressions/countExpression";
import { ApplyExpression, ODataApplyAggregate } from "./expressions/applyExpression";
import { SearchExpression } from "./expressions/searchExpression";
import { SelectExpression } from "./expressions/selectExpression";
import { InFunction } from "./expressions/inFunction";
import { ContainsFunction } from "./expressions/containsFunction";
import { LikeFunction } from "./expressions/likeFunction";
import { StartsWithFunction } from "./expressions/startsWithFunction";
import { EndsWithFunction } from "./expressions/endsWithFunction";
import { OrderByFunction } from "./expressions/orderByExpression";
import { NotContainsFunction } from "./expressions/notContainsFunction";

export class ODataFilterBuilder<T extends object = Record<string, unknown>>
{
    private readonly _expressions: Expression[];
    private readonly _filters: Expression[];
    constructor()
    {
        this._expressions = [];
        this._filters = [];
    }

    constantFilter(constant: string): this
    {
        this._filters.push(new ConstantExpression(constant));
        return this;
    }

    filter(field: keyof T, op: Condition, value: string | number | boolean | Date): this
    {
        this._filters.push(new BooleanExpression(field as string, op, value));

        return this;
    }

    filterEQ(field: keyof T, value: string | number | boolean | Date): this
    {
        return this.filter(field, Condition.EQ, value);
    }

    filterNE(field: keyof T, value: string | number | boolean | Date): this
    {
        return this.filter(field, Condition.NE, value);
    }

    filterGT(field: keyof T, value: string | number | Date): this
    {
        return this.filter(field, Condition.GT, value);
    }

    filterGE(field: keyof T, value: string | number | Date): this
    {
        return this.filter(field, Condition.GE, value);
    }

    filterLT(field: keyof T, value: string | number | Date): this
    {
        return this.filter(field, Condition.LT, value);
    }

    filterLE(field: keyof T, value: string | number | Date): this
    {
        return this.filter(field, Condition.LE, value);
    }

    in(field: keyof T, value: string[] | number[] | Date[]): this
    {
        this._filters.push(new InFunction(field as string, value));
        return this;
    }

    contains(field: keyof T, value: string): this
    {
        this._filters.push(new ContainsFunction(field as string, value));
        return this;
    }

    notContains(field: keyof T, value: string): this
    {
        this._filters.push(new NotContainsFunction(field as string, value));
        return this;
    }

    like(field: keyof T, value: string): this
    {
        this._filters.push(new LikeFunction(field as string, value));
        return this;
    }

    startsWith(field: keyof T, value: string): this
    {
        this._filters.push(new StartsWithFunction(field as string, value));

        return this;
    }

    endsWith(field: keyof T, value: string): this
    {
        this._filters.push(new EndsWithFunction(field as string, value));

        return this;
    }

    top(value: number): this
    {
        this._expressions.push(new TopExpression(value));

        return this;
    }

    skip(value: number): this
    {
        this._expressions.push(new SkipExpression(value));

        return this;
    }

    count(): this
    {
        this._expressions.push(new CountExpression());

        return this;
    }

    apply(aggregates: ODataApplyAggregate[]): this
    {
        this._expressions.push(new ApplyExpression(aggregates));
        return this;
    }

    search(value: string): this
    {
        this._expressions.push(new SearchExpression(value));

        return this;
    }

    select(value: string[]): this
    {
        this._expressions.push(new SelectExpression(value));

        return this;
    }

    orderby(field: string, value?: string): this
    {
        this._expressions.push(new OrderByFunction(field, value));

        return this;
    }

    build(): string
    {
        if (this._expressions.length === 0 && this._filters.length === 0)
        {
            return "";
        }

        // TODO - does order matter here?
        const queryFragments = buildQueryFragments(this._expressions);

        const filters = buildFilterFragments(this._filters);

        //make sure to manual encode + signs since our decoder will replace them with spaces if not encoded and they
        // don't seem to be automatically encoded since they can be used to represent space in a url
        return `${queryFragments}&${filters}`;

        function buildQueryFragments(expressions: Expression[]): string
        {
            return expressions.map(x => x.toQuery()).join("&");
        }

        function buildFilterFragments(expressions: Expression[]): string
        {
            if (!expressions || expressions.length < 1)
            {
                return "";
            }

            const filters = expressions.map(x => x.toQuery()).join(" and ");
            return `$filter=${filters}`;
        }
    }

    filterString(): string
    {
        if (!this._filters || this._filters.length < 1)
            return "";
        return this._filters.map(x => x.toQuery()).join(" and ");
    }
}
